import React from 'react';
import { Close } from '@mui/icons-material';
import { Box, Dialog, IconButton, styled } from '@mui/material';
import { Media } from '@askporter/utils';

// Styled components for image preview dialog
const ImageHeader = styled(Box)({
  zIndex: 2,
  background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  color: '#FFFFFF',
});

const ImageBackground = styled(Box)({
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  position: 'absolute',
  top: '-10px',
  bottom: '-10px',
  left: '-10px',
  right: '-10px',
  filter: 'blur(6px) brightness(0.4)',
});

const ImageContainer = styled(Box)({
  display: 'flex',
  flexGrow: 1,
  zIndex: 1,
  justifyContent: 'center',
  img: {
    objectFit: 'contain',
  },
});

export interface PreviewImageProps {
  /**
   * The image to preview
   */
  image: Media | undefined;
  /**
   * Callback to close the preview
   */
  onClose: () => void;
}

/**
 * PreviewImage displays a full-screen dialog with an image or video preview
 * Features a blurred background, gradient header, and close button
 */
export const PreviewImage: React.FC<PreviewImageProps> = ({ image, onClose }) => {
  const isVideo = image?.nameAndType?.type === 'mp4' || image?.nameAndType?.type === 'hevc';
  const mediaSrc = image?.path || image?.tempPath;
  const mediaType = isVideo ? 'video' : 'image';
  const fileName = image?.nameAndType?.name || `Media ${mediaType}`;

  const handleClose = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  return (
    <Dialog
      fullScreen
      open={!!image}
      onClose={onClose}
      aria-labelledby="media-preview-title"
      aria-describedby="media-preview-description"
      disableScrollLock
      slotProps={{
        paper: {
          sx: {
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box id="media-preview-title" sx={{ position: 'absolute', left: '-9999px' }}>
        {`${fileName} preview`}
      </Box>
      <Box id="media-preview-description" sx={{ position: 'absolute', left: '-9999px' }}>
        {isVideo ? 'Video preview with playback controls' : 'Full screen image preview'}
      </Box>
      <ImageHeader>
        <IconButton
          onClick={handleClose}
          aria-label={`Close ${fileName} preview`}
          sx={{
            padding: '16px',
            color: '#ffffff',
            margin: '6px',
          }}
        >
          <Close />
        </IconButton>
      </ImageHeader>
      <ImageContainer>
        {isVideo ? (
          <video
            controls
            autoPlay
            aria-label={`${fileName} video player`}
            style={{ maxWidth: '70vw', maxHeight: '80vh' }}
            src={mediaSrc}
          >
            <track kind="captions" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img style={{ maxWidth: '70vw' }} alt={fileName} src={mediaSrc} />
        )}
      </ImageContainer>
      <ImageBackground aria-hidden="true" sx={{ backgroundImage: `url(${mediaSrc || ''})` }} />
    </Dialog>
  );
};
